import React from 'react';
import { useThemeContext } from '../contexts/ThemeContext.jsx';
import { Button, Card, CardHeader, CardBody, Input, Badge } from '../components';

function ThemeSwitcher() {
  const { currentTheme, switchTheme } = useThemeContext();

  const handleThemeSwitch = (themeId) => {
    console.log('ThemeSwitcher: Switching to theme:', themeId);
    switchTheme(themeId);
  };

  // Function to show current CSS variables for debugging
  const showCurrentThemeValues = () => {
    const root = document.documentElement;
    const computedStyle = getComputedStyle(root);
    const primary = computedStyle.getPropertyValue('--color-primary');
    const accent = computedStyle.getPropertyValue('--color-accent');
    const heading = computedStyle.getPropertyValue('--font-family-heading');
    
    alert(`Current Theme Values:
    --color-primary: ${primary}
    --color-accent: ${accent}
    --font-family-heading: ${heading}
    Body classes: ${document.body.className}`);
  };

  return (
    <div className="flex-1 flex flex-col items-center justify-center px-4 py-8">
      <div className="max-w-4xl mx-auto text-center">
        <h1 className="text-6xl font-heading font-black mb-6 text-primary">
          Versatiles Bakery
        </h1>
        <p className="text-xl text-text-secondary mb-8 font-body">
          Multi-store bakery platform with dynamic theme switching
        </p>
        
        <div className="mb-8">
          <p className="text-lg mb-4 text-text-primary">
            Current Theme: <span className="font-semibold text-accent">{currentTheme?.name}</span>
          </p>
          <p className="text-sm text-text-muted mb-6">
            {currentTheme?.description}
          </p>
        </div>

        <div className="flex flex-wrap gap-4 justify-center mb-12">
          <Button
            onClick={() => handleThemeSwitch('default')}
            variant="secondary"
            size="lg"
          >
            Default Theme
          </Button>
          <Button
            onClick={() => handleThemeSwitch('antic')}
            variant="primary"
            size="lg"
          >
            Antic Theme
          </Button>
          <Button
            onClick={showCurrentThemeValues}
            variant="outline"
            size="lg"
          >
            Debug CSS Variables
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto mb-12">
          <Card hover shadow>
            <CardHeader>
              <h3 className="text-xl font-heading font-bold text-primary flex items-center">
                ⚡ Runtime Switching
                <Badge variant="accent" size="sm" className="ml-2">New</Badge>
              </h3>
            </CardHeader>
            <CardBody>
              <p className="text-text-secondary">
                Switch themes instantly without page reloads using our advanced CSS variable system.
              </p>
            </CardBody>
          </Card>
          
          <Card hover shadow>
            <CardHeader>
              <h3 className="text-xl font-heading font-bold text-primary flex items-center">
                🎨 Designer First
                <Badge variant="primary" size="sm" className="ml-2">Core</Badge>
              </h3>
            </CardHeader>
            <CardBody>
              <p className="text-text-secondary">
                Store folders contain only design tokens, assets, and CSS overrides for easy customization.
              </p>
            </CardBody>
          </Card>
          
          <Card hover shadow>
            <CardHeader>
              <h3 className="text-xl font-heading font-bold text-primary flex items-center">
                🏪 Unlimited Stores
                <Badge variant="success" size="sm" className="ml-2">Pro</Badge>
              </h3>
            </CardHeader>
            <CardBody>
              <p className="text-text-secondary">
                Single codebase supporting multiple bakery brands with unique visual identities.
              </p>
            </CardBody>
          </Card>
        </div>

        {/* Component Showcase */}
        <Card className="max-w-2xl mx-auto">
          <CardHeader>
            <h3 className="text-2xl font-heading font-bold text-primary">
              Component Showcase
            </h3>
            <p className="text-text-secondary mt-2">
              Demonstrating our design system components
            </p>
          </CardHeader>
          <CardBody className="space-y-6">
            {/* Buttons */}
            <div>
              <h4 className="text-lg font-semibold mb-3">Buttons</h4>
              <div className="flex flex-wrap gap-2">
                <Button variant="primary">Primary</Button>
                <Button variant="secondary">Secondary</Button>
                <Button variant="accent">Accent</Button>
                <Button variant="outline">Outline</Button>
                <Button variant="ghost">Ghost</Button>
              </div>
            </div>

            {/* Badges */}
            <div>
              <h4 className="text-lg font-semibold mb-3">Badges</h4>
              <div className="flex flex-wrap gap-2">
                <Badge variant="default">Default</Badge>
                <Badge variant="primary">Primary</Badge>
                <Badge variant="accent">Accent</Badge>
                <Badge variant="success">Success</Badge>
                <Badge variant="warning">Warning</Badge>
                <Badge variant="error">Error</Badge>
              </div>
            </div>

            {/* Input */}
            <div>
              <h4 className="text-lg font-semibold mb-3">Form Elements</h4>
              <div className="space-y-4">
                <Input 
                  label="Email Address" 
                  placeholder="Enter your email" 
                  type="email"
                />
                <Input 
                  label="Newsletter Signup" 
                  placeholder="Stay updated with our latest products"
                  helperText="We'll never share your email with anyone else."
                />
              </div>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}

export default ThemeSwitcher;
